import * as React from "react";
import { Check, Copy } from "@/components/ui/icon";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

export interface JsonViewerProps {
  /**
   * Either a value to stringify or a raw JSON string. Strings are parsed and
   * re-formatted when valid; if parsing fails the raw text is shown verbatim.
   */
  data: unknown;
  /** Indentation width for pretty-printing. */
  indent?: number;
  /** Hide the copy-to-clipboard button. */
  hideCopy?: boolean;
  /** Constrain height and make the body scroll. */
  maxHeightClassName?: string;
  className?: string;
}

function toPretty(data: unknown, indent: number): string {
  if (typeof data === "string") {
    try {
      return JSON.stringify(JSON.parse(data), null, indent);
    } catch {
      return data;
    }
  }
  try {
    return JSON.stringify(data, null, indent);
  } catch {
    return String(data);
  }
}

/** Read-only pretty-printed JSON block with copy support (spec §23 Agent Runs). */
export function JsonViewer({
  data,
  indent = 2,
  hideCopy = false,
  maxHeightClassName = "max-h-[28rem]",
  className,
}: JsonViewerProps) {
  const text = React.useMemo(() => toPretty(data, indent), [data, indent]);
  const [copied, setCopied] = React.useState(false);

  const copy = React.useCallback(() => {
    if (typeof navigator === "undefined" || !navigator.clipboard) return;
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  }, [text]);

  return (
    <div
      className={cn(
        "relative rounded-md border border-border bg-muted/40",
        className,
      )}
    >
      {!hideCopy ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={copy}
          aria-label="Copy JSON"
          className="absolute right-2 top-2 z-10 h-7 w-7"
        >
          {copied ? (
            <Check className="h-4 w-4" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </Button>
      ) : null}
      <ScrollArea className={cn(maxHeightClassName)}>
        <pre className="overflow-x-auto p-4 text-xs leading-relaxed">
          <code className="font-mono">{text}</code>
        </pre>
      </ScrollArea>
    </div>
  );
}

export default JsonViewer;
